import {
  AuthState,
  LoginError,
  LoginParams,
  LoginResponse,
  TokenResponse,
  Verify2FAParams,
  Verify2FAResponse,
} from "@/types/auth";
import { Dispatch, PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { connectSocket, disconnectSocket } from "../../redux/websocketMiddleware";
import { getCookie, setCookie } from "@/utils/cookieUtils";
import {
  loginApi,
  refreshJWT,
  revokeTokens,
  validateJWT,
  verify2FAApi,
  send2FACodeEmailApi,
  send2FARecoveryEmailApi,
  verify2FAResetCodeApi,
} from "@/services/authService";

import { AxiosError } from "axios";
import { RootState } from "@/redux/store";
import { isAuthenticated, getOrCreateSessionId, extractUserId } from "@/utils/auth";
import { getDeviceUUID } from "@/utils/deviceUUID";
import { invalidateOrgUsersCache } from "@/services/userGroupService";
import { clearMediaCache } from "@/utils/mediaCache";

const cookieExpiry = 30;

// 2FA phase constants matching backend
const TYPE_2FA_RESPONSE_NEEDED = "2fa_needed";
const TYPE_RESPONSE_RESET_PASSWORD_NEEDED = "reset_password_needed";

/**
 * Resolves the numeric userId after a 2FA / reset-code success. Some router builds omit the
 * numeric id from the verify response; when the seed is empty we re-fetch it via validateJWT
 * (routerId/username come from auth state, supplied by the callers). Redundant if the verify
 * response reliably carries `userId` — the seed then short-circuits validateJWT.
 */
async function resolveNumericUserIdAfter2FA(
  responseUserId: number | undefined,
  username: string | null | undefined,
  routerId: number | null | undefined,
): Promise<number | undefined> {
  let userId = responseUserId || undefined;
  if (userId || !username || !routerId) return userId;
  try {
    const validateResponse = await validateJWT(username, routerId);
    if (validateResponse.status === 200) {
      userId = extractUserId(validateResponse.data) ?? userId;
    }
  } catch (error) {
    console.error("JWT validation failed after 2FA:", error);
  }
  if (!userId) {
    console.warn("[auth] Could not resolve a numeric userId after 2FA — E2EE may stay inactive until refresh");
  }
  return userId;
}

/** Dispatches connectSocket only for a real numeric userId (the socket needs one for WS auth). */
function maybeConnectSocket(dispatch: Dispatch, userId: number | undefined): void {
  if (userId) {
    dispatch(connectSocket({ userId, sessionId: getOrCreateSessionId() }));
  }
}

/**
 * Async thunk for logging in a user.
 *
 * @param {Omit<LoginParams, 'deviceId' | 'trustDevice'>} loginParams - The login parameters (routerId, username, password).
 * @returns {Promise<LoginResponse>} - The login response from making a request to validate a user's credentials.
 */
export const login = createAsyncThunk<
  LoginResponse,
  Omit<LoginParams, 'deviceId' | 'trustDevice'>,
  { rejectValue: LoginError }
>(
  "auth/login",
  async (loginParams, { dispatch, rejectWithValue }) => {
    try {
      const deviceId = getDeviceUUID();
      const trustDevice = localStorage.getItem('trustDevice') === 'true';
      
      const fullLoginParams: LoginParams = {
        ...loginParams,
        deviceId,
        trustDevice,
      };
      
      const response = await loginApi(fullLoginParams);

      // Check if password reset is needed
      if (response.phase === TYPE_RESPONSE_RESET_PASSWORD_NEEDED) {
        // TODO: Handle password reset flow
        return rejectWithValue({
          message: "Password reset required",
        });
      }

      // Check if 2FA is required
      if (response.phase === TYPE_2FA_RESPONSE_NEEDED) {
        // Return response with 2FA flag - don't connect socket yet
        return response;
      }

      // Now check authentication for non-2FA flows
      if (!isAuthenticated(response)) {
        return rejectWithValue({
          message: response.error ?? "User authentication failed",
        });
      }

      // Validate JWT and retrieve the numeric userId.
      // The login response now includes userId from addCredentials, but we also
      // confirm via validateJWT so the JWT cookie is guaranteed set before the
      // socket connects (which needs it for WebSocket auth).
      let userId: number | null = response.userId; // set by PHP addCredentials
      try {
        const validateResponse = await validateJWT(response.username, response.routerId);
        if (validateResponse.status === 200) {
          userId = extractUserId(validateResponse.data) ?? userId;
        }
      } catch (error) {
        console.error("JWT validation failed after login:", error);
        // Continue anyway - the login was successful, validation might fail due to timing
      }

      maybeConnectSocket(dispatch, userId ?? undefined);

      return { ...response, userId };
    } catch (error) {
      const err = error as AxiosError<LoginResponse>;
      return rejectWithValue({
        message: err.response?.data?.error || err.message,
      });
    }
  }
);

/**
 * Asynchronous thunk action to check the authentication status of the user.
 *
 * This function performs the following:
 * 1. Retrieves `username` and `routerId` from cookies.
 * 2. Validates the JWT.
 * 3. If the JWT is valid (response status 200), returns the `username` and `routerId`.
 * 4. If JWT validation fails, attempts to refresh the JWT using the `refreshJWT` function.
 * 5. If the refresh token request is successful, returns the `username` and `routerId`.
 * 6. Logs errors and returns a rejection value if either JWT validation or refresh fails.
 *
 * @returns {Promise<Object | undefined>} Resolves with an object containing `username` and `routerId` if successful, or rejects with an error message.
 */
export const checkAuthStatus = createAsyncThunk(
  "auth/checkAuthStatus",
  async (_, { dispatch, rejectWithValue }) => {
    const username = getCookie("username") as string;
    const routerId = Number(getCookie("routerId"));

    if (!username || !routerId) {
      return rejectWithValue("Username and routerId not found in cookies");
    }

    // Note: We don't check for uaw-jwt cookie here because it's HttpOnly
    // and cannot be read by JavaScript. The backend will validate it automatically
    // when we make the validateJWT request with withCredentials: true

    try {
      const response = await validateJWT(username, routerId);
      if (response.status === 200) {
        const userId = extractUserId(response.data);
        const firstName = response.data.firstName || "";
        const lastName = response.data.lastName || "";

        maybeConnectSocket(dispatch, userId);

        return {
          username,
          routerId,
          userId,
          firstName,
          lastName,
        } as TokenResponse;
      }

      // If JWT validation fails, throw an error to be caught by the catch block
      throw new Error(response.data.error);
    } catch (_) {
      try {
        const refreshResponse = await refreshJWT(username, routerId);
        if (refreshResponse.status === 200) {
          const userId = extractUserId(refreshResponse.data);
          const firstName = refreshResponse.data.firstName || "";
          const lastName = refreshResponse.data.lastName || "";

          maybeConnectSocket(dispatch, userId);

          return {
            username,
            routerId,
            userId,
            firstName,
            lastName,
          } as TokenResponse;
        }

        return rejectWithValue(refreshResponse.data.error);
      } catch (refreshError) {
        // Log and return error message from refresh attempt
        const err = refreshError as Error;
        return rejectWithValue(err.message || "An error occurred");
      }
    }
  }
);

/**
 * Async thunk for verifying 2FA code.
 *
 * @param {Omit<Verify2FAParams, 'deviceId' | 'trustDevice'>} params - The 2FA verification parameters.
 * @returns {Promise<Verify2FAResponse>} - The verification response.
 */
export const verify2FA = createAsyncThunk<
  Verify2FAResponse,
  Omit<Verify2FAParams, 'deviceId' | 'trustDevice'> & { trustDevice?: boolean },
  { rejectValue: LoginError }
>(
  "auth/verify2FA",
  async (params, { dispatch, rejectWithValue, getState }) => {
    try {
      const deviceId = getDeviceUUID();
      const password = (getState() as RootState).auth.pendingPassword;

      const fullParams: Verify2FAParams = {
        ...params,
        deviceId,
        trustDevice: params.trustDevice ?? false,
        ...(password && { password }),
      };

      const response = await verify2FAApi(fullParams);

      if (!response.success) {
        return rejectWithValue({
          message: response.error ?? "2FA verification failed",
        });
      }

      const authState = (getState() as RootState).auth;
      const userId = await resolveNumericUserIdAfter2FA(
        response.userId,
        response.username ?? authState.username,
        response.routerId ?? authState.routerId,
      );

      maybeConnectSocket(dispatch, userId);

      return { ...response, userId };
    } catch (error) {
      const err = error as AxiosError<Verify2FAResponse>;
      return rejectWithValue({
        message: err.response?.data?.error || err.message,
      });
    }
  }
);

/**
 * Async thunk for sending 2FA code via email (for login/setup).
 *
 * @returns {Promise<{success: boolean, message: string}>} - The response from the API.
 */
export const send2FACodeEmail = createAsyncThunk<
  { success: boolean; message: string },
  void,
  { rejectValue: LoginError }
>(
  "auth/send2FACodeEmail",
  async (_, { rejectWithValue, getState }) => {
    try {
      const password = (getState() as RootState).auth.pendingPassword;
      const response = await send2FACodeEmailApi(password ?? undefined);

      if (!response.success) {
        return rejectWithValue({
          message: response.message || "Failed to send 2FA email",
        });
      }

      return response;
    } catch (error) {
      const err = error as AxiosError<{ success: boolean; message: string }>;
      return rejectWithValue({
        message: err.response?.data?.message || err.message || "Failed to send 2FA email",
      });
    }
  }
);

/**
 * Async thunk for sending 2FA recovery email.
 *
 * @param {Object} params - Parameters containing username.
 * @returns {Promise<{success: boolean, message: string}>} - The response from the API.
 */
export const send2FARecoveryEmail = createAsyncThunk<
  { success: boolean; message: string },
  void,
  { rejectValue: LoginError }
>(
  "auth/send2FARecoveryEmail",
  async (_, { rejectWithValue, getState }) => {
    try {
      const password = (getState() as RootState).auth.pendingPassword;
      const response = await send2FARecoveryEmailApi(password ?? undefined);

      if (!response.success) {
        return rejectWithValue({
          message: response.message || "Failed to send recovery email",
        });
      }

      return response;
    } catch (error) {
      const err = error as AxiosError<{ success: boolean; message: string }>;
      return rejectWithValue({
        message: err.response?.data?.message || err.message || "Failed to send recovery email",
      });
    }
  }
);

/**
 * Async thunk for verifying 2FA reset code (recovery flow).
 *
 * @param {Object} params - Parameters for reset code verification.
 * @returns {Promise<Verify2FAResponse>} - The verification response.
 */
export const verify2FAResetCode = createAsyncThunk<
  Verify2FAResponse,
  { code: string; trustDevice?: boolean },
  { rejectValue: LoginError }
>(
  "auth/verify2FAResetCode",
  async (params, { dispatch, rejectWithValue, getState }) => {
    try {
      const deviceId = getDeviceUUID();
      const password = (getState() as RootState).auth.pendingPassword;

      const response = await verify2FAResetCodeApi({
        code: params.code,
        deviceId,
        trustDevice: params.trustDevice ?? false,
        ...(password && { password }),
      });

      if (!response.success) {
        return rejectWithValue({
          message: response.message || "Reset code verification failed",
        });
      }

      const authState = (getState() as RootState).auth;
      const userId = await resolveNumericUserIdAfter2FA(
        response.userId,
        response.username ?? authState.username,
        response.routerId ?? authState.routerId,
      );

      maybeConnectSocket(dispatch, userId);

      return { ...response, userId };
    } catch (error) {
      const err = error as AxiosError<Verify2FAResponse>;
      return rejectWithValue({
        message: err.response?.data?.message || err.message || "Reset code verification failed",
      });
    }
  }
);

/**
 * Creates an asynchronous thunk action to log out the user.
 * Revokes JWT + refresh tokens, and removes user info from cookies.
 */
export const logout = createAsyncThunk("auth/logout", async (_, { dispatch, getState }) => {
  try {
    // Clear E2EE sessionStorage for the outgoing user so the next account
    // starts auto-share fresh (avoids cross-account cache bleed).
    const state = getState() as RootState;
    const uid = state.auth.userId;
    if (uid) {
      sessionStorage.removeItem(`e2ee:autoShare:v2:lastSharedKeyId:${uid}`);
    }

    invalidateOrgUsersCache();
    clearMediaCache(); // revoke decoded attachment blobs so they don't bleed across accounts
    await revokeTokens();
    dispatch(disconnectSocket());
    return;
  } catch (error) {
    console.error(error);
  }
});

const loadPersistedAuthMethod = (): 'authenticator' | 'email' | null => {
  const stored = localStorage.getItem('preferredAuthMethod');
  if (stored === 'email' || stored === 'authenticator') {
    return stored;
  }
  return null;
};

const initialState: AuthState = {
  isAuthenticated: false,
  routerId: null,
  username: null,
  userId: null,
  firstName: null,
  lastName: null,
  error: null,
  requires2FA: false,
  qrCodeUrl: null,
  isFirstTime2FA: false,
  remember2FADays: 30, // Default value
  preferredAuthMethod: loadPersistedAuthMethod(),
  routerE2eeEnabled: true, // default true until router info says otherwise
  pendingPassword: null,
};

/**
 * Auth slice to manage authentication state.
 */
export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setRouterId(state: AuthState, action: PayloadAction<number>) {
      state.routerId = action.payload;
    },
    setAuthState: (state: AuthState, action: PayloadAction<AuthState>) => {
      return { ...state, ...action.payload };
    },
    clearAuthError(state: AuthState) {
      state.error = null;
    },
    setPreferredAuthMethod: (state: AuthState, action: PayloadAction<'authenticator' | 'email'>) => {
      state.preferredAuthMethod = action.payload;
      localStorage.setItem('preferredAuthMethod', action.payload);
    },
  },
  extraReducers: (builder) => {
    // login reducers
    builder.addCase(
      login.fulfilled,
      (state: AuthState, action) => {
        const { username, routerId, userId, firstName, lastName, phase, qrCodeUrl, router_info } =
          action.payload;

        // Extract remember2FA value from router_info (default to 30 if not provided)
        const remember2FADays = router_info?.remember2FA ?? 30;
        const routerE2eeEnabled = router_info?.e2ee_enabled ?? true;

        // If 2FA is required, set intermediate state
        if (phase === TYPE_2FA_RESPONSE_NEEDED) {
          state.isAuthenticated = false;
          state.requires2FA = true;
          state.qrCodeUrl = qrCodeUrl || null;
          // First time 2FA if QR code is provided
          state.isFirstTime2FA = !!qrCodeUrl;

          if (state.isFirstTime2FA) {
            state.preferredAuthMethod = null;
            localStorage.removeItem('preferredAuthMethod');
          }

          state.username = username;
          state.routerId = routerId;
          state.userId = userId;
          state.firstName = firstName;
          state.lastName = lastName;
          state.remember2FADays = remember2FADays;
          state.routerE2eeEnabled = routerE2eeEnabled;
          state.error = null;
          // Hold password in memory so 2FA-phase requests can re-submit it for auth.
          state.pendingPassword = action.meta.arg.password;
          return;
        }

        // Full authentication without 2FA
        state.isAuthenticated = true;
        state.requires2FA = false;
        state.qrCodeUrl = null;
        state.isFirstTime2FA = false;
        state.username = username;
        state.routerId = routerId;
        state.userId = userId;
        state.firstName = firstName;
        state.lastName = lastName;
        state.remember2FADays = remember2FADays;
        state.routerE2eeEnabled = routerE2eeEnabled;
        state.error = null;

        // Save username, routerId to cookies for persistence
        setCookie("username", username, cookieExpiry);
        setCookie("routerId", routerId.toString(), cookieExpiry);
      }
    );
    builder.addCase(login.rejected, (state: AuthState, action) => {
      state.isAuthenticated = false;
      state.requires2FA = false;
      state.qrCodeUrl = null;
      state.isFirstTime2FA = false;
      state.pendingPassword = null;
      state.error =
        action.payload?.message || "Authentication failed: Unknown error.";
    });

    // verify2FA reducers
    builder.addCase(
      verify2FA.fulfilled,
      (state: AuthState, action: PayloadAction<Verify2FAResponse>) => {
        const { username, routerId, userId, firstName, lastName } = action.payload;

        state.isAuthenticated = true;
        state.requires2FA = false;
        state.qrCodeUrl = null;
        state.isFirstTime2FA = false;
        state.pendingPassword = null;
        state.username = username || state.username;
        state.routerId = routerId || state.routerId;
        state.userId = userId || state.userId;
        state.firstName = firstName || state.firstName;
        state.lastName = lastName || state.lastName;
        state.error = null;

        // Save username, routerId to cookies for persistence
        if (state.username && state.routerId) {
          setCookie("username", state.username, cookieExpiry);
          setCookie("routerId", state.routerId.toString(), cookieExpiry);
        }
      }
    );
    builder.addCase(verify2FA.rejected, (state: AuthState, action) => {
      state.error =
        action.payload?.message || "2FA verification failed: Unknown error.";
    });

    // send2FACodeEmail reducers
    builder.addCase(send2FACodeEmail.fulfilled, (state: AuthState) => {
      state.error = null;
    });
    builder.addCase(send2FACodeEmail.rejected, (state: AuthState, action) => {
      state.error =
        action.payload?.message || "Failed to send 2FA email: Unknown error.";
    });

    // send2FARecoveryEmail reducers
    builder.addCase(send2FARecoveryEmail.fulfilled, (state: AuthState) => {
      state.error = null;
    });
    builder.addCase(send2FARecoveryEmail.rejected, (state: AuthState, action) => {
      state.error =
        action.payload?.message || "Failed to send recovery email: Unknown error.";
    });

    // verify2FAResetCode reducers
    builder.addCase(
      verify2FAResetCode.fulfilled,
      (state: AuthState, action: PayloadAction<Verify2FAResponse>) => {
        const { username, routerId, userId, firstName, lastName } =
          action.payload;

        state.isAuthenticated = true;
        state.requires2FA = false;
        state.qrCodeUrl = null;
        state.isFirstTime2FA = false;
        state.pendingPassword = null;
        state.username = username || state.username;
        state.routerId = routerId || state.routerId;
        state.userId = userId || state.userId;
        state.firstName = firstName || state.firstName;
        state.lastName = lastName || state.lastName;
        state.error = null;

        // Save username, routerId to cookies for persistence
        if (state.username && state.routerId) {
          setCookie("username", state.username, cookieExpiry);
          setCookie("routerId", state.routerId.toString(), cookieExpiry);
        }
      }
    );
    builder.addCase(verify2FAResetCode.rejected, (state: AuthState, action) => {
      state.error =
        action.payload?.message || "Reset code verification failed: Unknown error.";
    });

    // checkAuthStatus reducers
    builder.addCase(
      checkAuthStatus.fulfilled,
      (state: AuthState, action: PayloadAction<TokenResponse>) => {
        state.isAuthenticated = true;
        state.username = action.payload.username;
        state.routerId = action.payload.routerId;
        state.userId = action.payload.userId;
        state.firstName = action.payload.firstName;
        state.lastName = action.payload.lastName;
        state.error = null;
      }
    );
    builder.addCase(checkAuthStatus.rejected, (state: AuthState) => {
      // Guard: if the user authenticated via login() while this async check was
      // in-flight, don't overwrite their session — the login result wins.
      if (state.isAuthenticated) return;
      state.isAuthenticated = false;
      state.username = null;
      state.routerId = null;
      state.firstName = null;
      state.lastName = null;
      state.userId = null;
    });

    // logout reducer
    builder.addCase(logout.fulfilled, (state: AuthState) => {
      state.isAuthenticated = false;
      state.username = null;
      state.firstName = null;
      state.lastName = null;
      state.routerId = null;
      state.userId = null;
      state.error = null;
      state.requires2FA = false;
      state.qrCodeUrl = null;
      state.isFirstTime2FA = false;
      state.pendingPassword = null;

      // remove user info from cookies
      setCookie("username", "", -cookieExpiry);
      setCookie("routerId", "", -cookieExpiry);
    });
  },
});

export const { setRouterId, clearAuthError, setAuthState, setPreferredAuthMethod } = authSlice.actions;
export const selectIsAuth = (state: RootState) => state.auth.isAuthenticated;
export const selectRouterId = (state: RootState) => state.auth.routerId;
export const selectUsername = (state: RootState) => state.auth.username;
export const selectFirstName = (state: RootState) => state.auth.firstName;
export const selectLastName = (state: RootState) => state.auth.lastName;
export const selectRequires2FA = (state: RootState) => state.auth.requires2FA;
export const selectUserId = (state: RootState) => state.auth.userId;
export const selectQrCodeUrl = (state: RootState) => state.auth.qrCodeUrl;
export const selectIsFirstTime2FA = (state: RootState) => state.auth.isFirstTime2FA;
export const selectRemember2FADays = (state: RootState) => state.auth.remember2FADays;
export const selectPreferredAuthMethod = (state: RootState) => state.auth.preferredAuthMethod;
export const selectRouterE2eeEnabled = (state: RootState) => state.auth.routerE2eeEnabled;

export const selectAuthError = (state: RootState) => state.auth.error;
export default authSlice.reducer;
